import { useEffect, useRef } from 'react';
import { OrderSearchItem, OrderSearchResult } from '@/api/models';
import CopyIcon from '@/assets/icons/copy.svg?react';
import styles from './OrderInfoTooltip.module.scss';

interface IProps {
  order: OrderSearchResult;
  anchor: HTMLButtonElement;
  onClose: () => void;
}

const joinText = (values: (string | undefined)[]) =>
  values
    .map((value) => value?.trim())
    .filter(Boolean)
    .join(' ');

const SET_ORDER_TYPE_NAME = 'full set';

const setHeadings = new Map([
  ['iron', 'Iron Set'],
  ['utility', 'Utility Set'],
  ['wedge', 'Wedge Set'],
]);

const getGroupHeading = (item: OrderSearchItem) => {
  const category = item.category?.trim();
  const isFullSet = item.orderTypeName?.trim().toLowerCase() === SET_ORDER_TYPE_NAME;
  const setHeading = category && isFullSet ? setHeadings.get(category.toLowerCase()) : undefined;
  return setHeading || category || item.supplyType?.trim() || 'Order details';
};

const formatItem = (item: OrderSearchItem) => {
  if (item.supplyDescription?.trim()) {
    const quantity = item.qty == null ? '' : `Qty: ${item.qty}`;
    return joinText([item.supplyDescription, quantity]);
  }
  const model = joinText([item.family, item.model, item.ironClub, item.loft]);
  const shaft = item.shaft?.trim() || item.putterShaft?.trim();
  const shaftText = shaft ? `Shaft: ${shaft}` : '';
  const quantity = item.orderSupplyDetailId != null && item.qty != null ? `Qty: ${item.qty}` : '';
  return joinText([model, shaftText, quantity]);
};

export default function OrderInfoTooltip({ order, anchor, onClose }: IProps) {
  const popupRef = useRef<HTMLDivElement>(null);
  const anchorRect = anchor.getBoundingClientRect();
  const spaceAbove = Math.max(0, anchorRect.top - 20);
  const spaceBelow = Math.max(0, window.innerHeight - anchorRect.bottom - 20);
  const placeAbove = spaceAbove >= 180 || spaceAbove > spaceBelow;
  const position = {
    left: Math.max(12, Math.min(anchorRect.left, window.innerWidth - 552)),
    top: placeAbove ? undefined : anchorRect.bottom + 8,
    bottom: placeAbove ? window.innerHeight - anchorRect.top + 8 : undefined,
    maxHeight: placeAbove ? spaceAbove : spaceBelow,
  };
  const groups = new Map<string, string[]>();
  for (const item of order.items ?? []) {
    const text = formatItem(item);
    if (!text) continue;
    const heading = getGroupHeading(item);
    const lines = groups.get(heading) ?? [];
    lines.push(text);
    groups.set(heading, lines);
  }
  const fallback = order.headModels?.trim() || 'Order details are unavailable.';

  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (event.target instanceof Node && popupRef.current?.contains(event.target)) return;
      onClose();
    };
    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !anchor.contains(event.target) && !popupRef.current?.contains(event.target)) onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        anchor.focus();
      }
    };
    window.addEventListener('resize', onClose);
    window.addEventListener('scroll', handleScroll, true);
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('resize', onClose);
      window.removeEventListener('scroll', handleScroll, true);
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [anchor, onClose]);

  return (
    <div ref={popupRef} id="order-info-tooltip" role="tooltip" aria-label={`Order information for ${order.orderNo}`} className={styles.tooltip} style={position}>
      <button type="button" className={styles.reorder} disabled>
        Re-Order <CopyIcon aria-hidden="true" />
      </button>
      {Array.from(groups, ([heading, lines]) => (
        <section key={heading} className={styles.group}>
          <h3>{heading}</h3>
          {lines.map((line, index) => (
            <p key={index}>{line}</p>
          ))}
        </section>
      ))}
      {groups.size === 0 && <p className={styles.fallback}>{fallback}</p>}
    </div>
  );
}
